/**
 * AuthHubService: The Art of Story's sign-in through the DDD Auth Hub.
 *
 * Every Dive Deep Dialogues app signs in at the central hub
 * (auth.divedeepdialogues.com), so there is one account, and one place to
 * delete it. After sign-in the hub redirects back to /auth/callback with TWO
 * custom tokens in the URL fragment:
 *   - `token`      → the hub project (ddd-auth-hub-4ed14): identity + claims.
 *   - `spokeToken` → this app's project (ddd-art-of-story): the session
 *     the Firestore rules and the Knowledge Hub (Marlow) actually check.
 * The callback signs in to both. Signing the hub token into this app's auth
 * throws auth/custom-token-mismatch (different project), which is why the hub
 * mints a separate spoke token through its keyless service-account bridge.
 *
 * Ported from The Art of Conversation's (itself from The Unspoken Dialogue's),
 * with its guard on the hub address and its handling for the hub's "continue
 * as guest" return.
 *
 * Australian English throughout.
 */

import { auth, authHubAuth } from './firebase';
import { signInWithCustomToken } from 'firebase/auth';

const DEFAULT_HUB_URL = 'https://auth.divedeepdialogues.com';

// The hub address, from env only when it really is the hub. This app's .env
// once pointed VITE_AUTH_HUB_URL at the app's own Firebase host, which would
// send sign-in in a circle; anything that isn't the hub (or a local hub dev
// server) is ignored, loudly.
const HUB_HOSTS = new Set(['auth.divedeepdialogues.com', 'ddd-auth-hub-4ed14.web.app', 'ddd-auth-hub-4ed14.firebaseapp.com']);
export const resolveHubUrl = (raw) => {
  if (!raw) return DEFAULT_HUB_URL;
  try {
    const url = new URL(raw);
    const local = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
    if ((url.protocol === 'https:' && HUB_HOSTS.has(url.hostname)) || local) {
      return url.origin;
    }
  } catch { /* not a URL: fall through */ }
  console.warn(`[AuthHub] Ignoring VITE_AUTH_HUB_URL=${raw}: not the Auth Hub. Using ${DEFAULT_HUB_URL}.`);
  return DEFAULT_HUB_URL;
};

// Post-sign-in destinations are paths on this site only, never another
// origin: "/rosalind" yes, "//evil.example" or "https://…" no.
export const safeRedirectPath = (raw) =>
  typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//') && !raw.startsWith('/\\') ? raw : '/';

class AuthHubService {
  constructor() {
    this.hubUrl = resolveHubUrl(import.meta.env.VITE_AUTH_HUB_URL);
    // Must equal this app's key in the hub's SPOKE_CONFIG
    // (apps/auth-hub-setup/functions/spokeAdminApps.js) and its id in the
    // hub's app list (src/data/apps.js).
    this.appId = 'art-of-story';
    this.appName = 'The Art of Story';
  }

  /**
   * Auth Hub sign-in enabled? Mirrors the flag in firebase.js (on unless
   * explicitly 'false') so the two never disagree.
   */
  isEnabled() {
    return import.meta.env.VITE_USE_AUTH_HUB !== 'false';
  }

  /**
   * The URL the hub returns to: always the token-exchange callback on
   * whichever host the user is on (artofstory.divedeepdialogues.com
   * or ddd-art-of-story.web.app, both allow-listed on the hub), with
   * the post-sign-in destination as ?redirect=.
   */
  buildReturnUrl(redirectPath = null) {
    const base = `${window.location.origin}/auth/callback`;
    return redirectPath ? `${base}?redirect=${encodeURIComponent(redirectPath)}` : base;
  }

  redirectToLogin(redirectPath = null) {
    const returnUrl = this.buildReturnUrl(redirectPath);
    window.location.href = `${this.hubUrl}/login?app=${this.appId}&return=${encodeURIComponent(returnUrl)}`;
  }

  redirectToSignup(redirectPath = null) {
    const returnUrl = this.buildReturnUrl(redirectPath);
    window.location.href = `${this.hubUrl}/signup?app=${this.appId}&return=${encodeURIComponent(returnUrl)}`;
  }

  _getFragmentParams() {
    const hash = window.location.hash;
    if (!hash || hash.length <= 1) return new URLSearchParams();
    return new URLSearchParams(hash.substring(1));
  }

  /** Hub-audience token: fragment first, query string as a fallback. */
  getAuthToken() {
    return this._getFragmentParams().get('token') || new URLSearchParams(window.location.search).get('token');
  }

  /** Spoke-audience token (ddd-art-of-story): the one the rules accept. */
  getSpokeToken() {
    return this._getFragmentParams().get('spokeToken') || new URLSearchParams(window.location.search).get('spokeToken');
  }

  /** The hub's "Continue as guest" returns #mode=guest and no tokens. */
  isGuestReturn() {
    return this._getFragmentParams().get('mode') === 'guest';
  }

  /** Post-sign-in destination, as a path on this site (defaults home). */
  getRedirectPath() {
    return safeRedirectPath(new URLSearchParams(window.location.search).get('redirect'));
  }

  /**
   * Sign in with the custom tokens from the hub.
   *   1. Hub token   → authHubAuth (identity/claims). Non-fatal on failure.
   *   2. Spoke token → auth (ddd-art-of-story). REQUIRED.
   * @returns {Promise<{user, claims}>} the spoke user.
   */
  async handleTokenExchange() {
    const hubToken = this.getAuthToken();
    if (!hubToken) throw new Error('No sign-in token came back from the Dive Deep Dialogues account page.');
    const spokeToken = this.getSpokeToken();

    if (authHubAuth) {
      try {
        await signInWithCustomToken(authHubAuth, hubToken);
      } catch (hubErr) {
        console.warn('[AuthHub] Hub sign-in failed (non-critical):', hubErr?.message || hubErr);
      }
    }

    let credential;
    if (spokeToken) {
      credential = await signInWithCustomToken(auth, spokeToken);
    } else {
      // No spoke token means the hub's spoke config for art-of-story is
      // missing, or createCustomToken hasn't been redeployed, or its IAM
      // grants aren't in place. Try the hub token so the failure is explicit
      // (auth/custom-token-mismatch) rather than a silent "no token".
      console.warn('[AuthHub] No spokeToken in the callback: the hub spoke config for art-of-story may be missing.');
      credential = await signInWithCustomToken(auth, hubToken);
    }

    const user = credential.user;
    const { claims } = await user.getIdTokenResult();
    this.cleanUrl();
    return { user, claims };
  }

  /** Strip the tokens from the URL (and history) once used. */
  cleanUrl() {
    const url = new URL(window.location.href);
    ['token', 'spokeToken', 'app', 'redirect'].forEach((k) => url.searchParams.delete(k));
    url.hash = '';
    window.history.replaceState({}, '', url.toString());
  }

  /** Sign out of BOTH sessions (leaving one orphaned trips the next visit). */
  async signOut(redirectToHub = false) {
    await Promise.all([
      auth.signOut().catch((err) => console.warn('[AuthHub] Sign out failed:', err?.message || err)),
      authHubAuth
        ? authHubAuth.signOut().catch((err) => console.warn('[AuthHub] Hub sign out failed:', err?.message || err))
        : Promise.resolve(),
    ]);
    if (redirectToHub) {
      window.location.href = `${this.hubUrl}/logout?return=${encodeURIComponent(window.location.origin)}`;
    }
  }
}

export const authHubService = new AuthHubService();
export default authHubService;
